import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import Loader from '@/components/common/Loader';
import { applySeo } from '@/lib/seo';

const fmtDate = (p) => {
  const d = p.publish_date || (p.created_at || '').slice(0, 10);
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
};

// Renders admin-authored content: raw HTML if it contains tags, otherwise paragraphs.
function PostBody({ content }) {
  const text = content || '';
  if (/<\/?[a-z][\s\S]*>/i.test(text)) {
    return <div className="prose mt-6 max-w-none text-base leading-relaxed text-foreground/85 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:mt-8 [&_h3]:text-xl [&_h3]:mt-6 [&_p]:mt-4 [&_img]:rounded-xl [&_a]:underline" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return (
    <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground/85">
      {text.split(/\n\s*\n/).filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
    </div>
  );
}

export function Blog() {
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get('/blog').then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  useEffect(() => applySeo({
    title: 'The Journal · Stories & Styling · Gaze Decor',
    description: 'Styling guides, decor stories and inspiration from the Gaze Decor editorial team.',
    ogType: 'website',
  }), []);
  if (!posts) return <Loader />;
  return (
    <div className="container-gaze py-8">
      <p className="eyebrow mb-2">The Journal</p>
      <h1 className="font-display text-4xl">Stories & Styling</h1>
      <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="blog-grid">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="group overflow-hidden rounded-2xl border border-border bg-white" data-testid="blog-card">
            <div className="aspect-[16/10] overflow-hidden"><img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" /></div>
            <div className="p-5">
              <p className="eyebrow mb-2">{p.category}</p>
              <h2 className="font-display text-xl leading-snug">{p.title}</h2>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
              <p className="mt-3 text-[11px] text-muted-foreground">{fmtDate(p)}{p.author ? ` · ${p.author}` : ''}</p>
            </div>
          </Link>
        ))}
      </div>
      {posts.length === 0 && <p className="py-16 text-center text-sm text-muted-foreground">No stories published yet — check back soon.</p>}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/blog/${slug}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [slug]);
  useEffect(() => {
    if (!data || data === false) return undefined;
    const p = data.post;
    return applySeo({
      title: p.meta_title || `${p.title} · Gaze Decor Journal`,
      description: p.meta_description || p.excerpt,
      keywords: p.keywords,
      canonical: p.canonical_url,
      ogTitle: p.og_title || p.title,
      ogDescription: p.og_description || p.excerpt,
      ogImage: p.og_image || p.cover,
    });
  }, [data]);
  if (data === null) return <Loader />;
  if (data === false) return <div className="container-gaze py-20 text-center">Post not found</div>;
  const { post, related } = data;
  return (
    <article className="container-gaze py-8" data-testid="blog-post">
      <nav className="mb-4 flex items-center gap-1 text-xs text-muted-foreground"><Link to="/blog">Journal</Link><ChevronRight size={12} /><span className="text-foreground">{post.category || 'Story'}</span></nav>
      <div className="mx-auto max-w-3xl">
        <p className="eyebrow mb-2">{post.category}</p>
        <h1 className="font-display text-4xl leading-tight sm:text-5xl" data-testid="blog-post-title">{post.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">By {post.author}{fmtDate(post) ? ` · ${fmtDate(post)}` : ''}</p>
        {post.cover && <img src={post.cover} alt={post.title} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover" />}
        <PostBody content={post.content} />
      </div>
      {related?.length > 0 && (
        <div className="mx-auto mt-12 max-w-3xl"><h3 className="mb-4 text-xl font-semibold">More stories</h3><div className="grid gap-4 sm:grid-cols-3">{related.map((r) => <Link key={r.id} to={`/blog/${r.slug}`} className="group"><img src={r.cover} alt={r.title} className="aspect-[16/10] w-full rounded-xl object-cover" /><p className="mt-2 text-sm font-medium">{r.title}</p></Link>)}</div></div>
      )}
    </article>
  );
}

export function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(false)); }, [slug]);
  useEffect(() => {
    if (!page || page === false) return undefined;
    return applySeo({
      title: page.meta_title || `${page.title} · Gaze Decor`,
      description: page.meta_description || '',
      keywords: page.keywords,
      canonical: page.canonical_url,
      ogTitle: page.og_title || page.title,
      ogDescription: page.og_description || page.meta_description,
      ogImage: page.og_image,
      ogType: 'website',
    });
  }, [page]);
  if (page === null) return <Loader />;
  if (page === false) return <div className="container-gaze py-20 text-center">Page not found</div>;
  return (
    <div className="container-gaze py-10" data-testid="cms-page">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-4xl" data-testid="cms-page-title">{page.title}</h1>
        <PostBody content={page.content} />
      </div>
    </div>
  );
}
